import { useEffect, useState } from 'react'
import { useNotify } from 'ra-core'
import { Button } from '@internal/ui-kit/components/ui/button'
import { Checkbox } from '@internal/ui-kit/components/ui/checkbox'
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@internal/ui-kit/components/ui/drawer'
import { Input } from '@internal/ui-kit/components/ui/input'
import { Label } from '@internal/ui-kit/components/ui/label'
import { extractErrorMessage, httpClient } from '@internal/ui-kit/lib/httpClient'
import { percentDisplayValue, percentToFraction } from '@internal/ui-kit/lib/percentField'
import type { referralDiscountCodeSchema } from './schema'
import type { z } from 'zod'

type ReferralDiscountCode = z.infer<typeof referralDiscountCodeSchema>

/**
 * `patchReferralDiscountCode` (`retool-exports/user-detail/queries.json`,
 * `PATCH v2/admin/referrals/discount-codes/{referral_code_id}`) — ticket
 * feedback: "edit referral code parameters - discount amount and percent AS
 * IN RETOOL", i.e. exactly the source export's own field set
 * (`numberInputReferralDiscountAmount`/`numberInputReferrerDiscountAmount`/
 * `numberInputReferrerPercentage`/`checkboxIsMultiUse`), not new ones.
 * `referrer_discount_percent` uses this project's own percent-field
 * convention (round 5 feedback: "has to be PERCENT FIELD") — stored/PATCHed
 * as a decimal fraction, shown and edited as a percentage, via the same
 * `percentDisplayValue`/`percentToFraction` helpers `ResourceForm`'s
 * `PercentInput`/`EditableTable`'s inline percent input already share
 * (`lib/percentField.ts`, HATCH-69); not independently confirmed the real
 * API stores this one as a fraction rather than a whole number, see
 * MIGRATION-NOTES.md.
 */
export function EditReferralCodeDrawer({
  referralDiscountCode,
  onUpdated,
}: {
  referralDiscountCode: ReferralDiscountCode
  onUpdated: () => void
}) {
  const [open, setOpen] = useState(false)
  const [referralDiscountAmount, setReferralDiscountAmount] = useState('')
  const [referrerDiscountAmount, setReferrerDiscountAmount] = useState('')
  const [referrerDiscountPercent, setReferrerDiscountPercent] = useState('')
  const [isMultiUse, setIsMultiUse] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const notify = useNotify()

  useEffect(() => {
    if (!open) return
    setReferralDiscountAmount(referralDiscountCode.referral_discount_amount?.toString() ?? '')
    setReferrerDiscountAmount(referralDiscountCode.referrer_discount_amount?.toString() ?? '')
    const displayedPercent = percentDisplayValue(referralDiscountCode.referrer_discount_percent)
    setReferrerDiscountPercent(displayedPercent === '' ? '' : String(displayedPercent))
    setIsMultiUse(Boolean(referralDiscountCode.is_multi_use))
  }, [open, referralDiscountCode])

  const submit = async () => {
    if (!referralDiscountCode.referral_code_id) return
    setIsSubmitting(true)
    try {
      await httpClient(`/v2/admin/referrals/discount-codes/${referralDiscountCode.referral_code_id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          referral_code_id: referralDiscountCode.referral_code_id,
          referral_discount_amount: referralDiscountAmount === '' ? null : Number(referralDiscountAmount),
          referrer_discount_amount: referrerDiscountAmount === '' ? null : Number(referrerDiscountAmount),
          referrer_discount_percent: referrerDiscountPercent === '' ? null : percentToFraction(Number(referrerDiscountPercent)),
          is_multi_use: isMultiUse,
        }),
      })
      notify('Referral code updated', { type: 'success' })
      setOpen(false)
      onUpdated()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not update this referral code.'), { type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Drawer open={open} onOpenChange={setOpen} direction="right">
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} disabled={!referralDiscountCode.referral_code_id}>
        Edit referral code
      </Button>
      <DrawerContent className="w-full sm:max-w-md">
        <DrawerHeader>
          <DrawerTitle>Edit referral code</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="referral-discount-amount">Referral discount amount</Label>
            <Input
              id="referral-discount-amount"
              type="number"
              value={referralDiscountAmount}
              onChange={(event) => setReferralDiscountAmount(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="referrer-discount-amount">Referrer discount amount</Label>
            <Input
              id="referrer-discount-amount"
              type="number"
              value={referrerDiscountAmount}
              onChange={(event) => setReferrerDiscountAmount(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="referrer-discount-percent">Referrer discount percent (%)</Label>
            <Input
              id="referrer-discount-percent"
              type="number"
              value={referrerDiscountPercent}
              onChange={(event) => setReferrerDiscountPercent(event.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="is-multi-use" checked={isMultiUse} onCheckedChange={(checked) => setIsMultiUse(checked === true)} />
            <Label htmlFor="is-multi-use">Multi-use</Label>
          </div>
        </div>
        <DrawerFooter>
          <Button type="button" disabled={isSubmitting} onClick={submit}>
            Save
          </Button>
          <DrawerClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
